/**
 * Contexto global de autenticação.
 * Mantém o usuário logado (AuthUser) em estado e sincroniza com o storage.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { AuthStorage } from '../api/authStorage';
import type { AuthUser } from '../api/endpoints/auth';

export interface AuthContextValue {
  /** Dados do usuário logado ou null se não autenticado */
  user: AuthUser | null;
  /** true se há usuário no estado (pode estar desatualizado em relação ao storage até refresh) */
  isAuthenticated: boolean;
  /** Atualiza o usuário no contexto (ex.: após login) */
  setUser: (user: AuthUser | null) => void;
  /** Recarrega o usuário a partir do storage (ex.: após refresh da página) */
  refreshUser: () => void;
  /** Limpa apenas os dados do contexto; não limpa localStorage (use handleLogout do useAuthGate para logout completo) */
  clearUser: () => void;
  /** Verifica se o usuário tem role Admin */
  hasRoleAdmin: boolean;
  /** Verifica se o usuário tem role Student */
  hasRoleStudent: boolean;
  /** Se tem ambos os perfis (Admin e Student), pode alternar no header */
  hasBothProfiles: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  authStorage: AuthStorage;
  children: ReactNode;
}

export function AuthProvider({ authStorage, children }: AuthProviderProps) {
  const [user, setUserState] = useState<AuthUser | null>(() =>
    authStorage.getAuthData()
  );

  const refreshUser = useCallback(() => {
    setUserState(authStorage.getAuthData());
  }, [authStorage]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const setUser = useCallback((next: AuthUser | null) => {
    setUserState(next);
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
  }, []);

  const roles = user?.roles ?? [];
  const hasRoleAdmin = roles.includes('Admin');
  const hasRoleStudent = roles.includes('Student');
  const hasBothProfiles = hasRoleAdmin && hasRoleStudent;

  const value: AuthContextValue = {
    user,
    isAuthenticated: user != null,
    setUser,
    refreshUser,
    clearUser,
    hasRoleAdmin,
    hasRoleStudent,
    hasBothProfiles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return ctx;
}

/** Retorna o redirect path após login: /admin (dashboard) só Admin, /student (home) para Student ou ambos */
export function getLoginRedirectPath(user: AuthUser | null): string {
  if (!user?.roles?.length) return '/admin';
  const hasAdmin = user.roles.includes('Admin');
  const hasStudent = user.roles.includes('Student');
  // Se tem perfil Student (sozinho ou com Admin), vai para home do student; senão dashboard admin
  if (hasStudent) return '/student';
  if (hasAdmin) return '/admin';
  return '/student';
}
